import { Controller } from '@hotwired/stimulus';

/**
 * Command-palette-style contract selector for the "Déposer une facture" modal.
 * Handles client-side search/filter, match highlighting, and keyboard navigation.
 *
 * Targets:
 * - search: the text input
 * - item: each contract link in "Tous les marchés" (used for search + keyboard nav)
 * - recentItem: each contract link in "Derniers utilisés" (excluded from search results)
 * - recentSection: the "Derniers utilisés" wrapper (hidden during search)
 * - allSection: the "Tous les marchés" wrapper
 * - resultCount: paragraph showing "N résultats" during search
 * - emptyState: the "Aucun marché trouvé" block
 * - itemText: text elements within items for match highlighting
 *
 * Actions:
 * - filter: triggered on search input, filters items by query
 * - handleKeydown: keyboard navigation (↑↓ Enter Escape)
 * - reset: clears search and restores all items
 *
 * @example
 * <div data-controller="contract-selector">
 *   <input data-contract-selector-target="search"
 *          data-action="input->contract-selector#filter keydown->contract-selector#handleKeydown"
 *          type="text" placeholder="Rechercher un marché...">
 *   <div data-contract-selector-target="recentSection">
 *     <a data-contract-selector-target="recentItem" href="...">Recent contract</a>
 *   </div>
 *   <div data-contract-selector-target="allSection">
 *     <a data-contract-selector-target="item" data-search-text="lot 1 plomberie" href="...">
 *       <span data-contract-selector-target="itemText">Lot 1 - Plomberie</span>
 *     </a>
 *   </div>
 *   <p data-contract-selector-target="resultCount" class="hidden"></p>
 *   <div data-contract-selector-target="emptyState" class="hidden">Aucun marché trouvé</div>
 * </div>
 */
export default class extends Controller {
  static targets = [
    'search',
    'item',
    'recentItem',
    'recentSection',
    'allSection',
    'resultCount',
    'emptyState',
    'itemText',
  ];

  connect() {
    this.highlightIndex = -1;

    // Auto-focus search when modal opens
    const modal = this.element.closest('[data-controller="modal"]');
    if (modal) {
      this._onModalOpen = () => {
        requestAnimationFrame(() => {
          this.searchTarget.focus();
          this.searchTarget.value = '';
          this.reset();
        });
      };
      modal.addEventListener('modal:open', this._onModalOpen);
    }
  }

  disconnect() {
    const modal = this.element.closest('[data-controller="modal"]');
    if (modal && this._onModalOpen) {
      modal.removeEventListener('modal:open', this._onModalOpen);
    }
  }

  filter() {
    const query = this.normalize(this.searchTarget.value.trim());

    if (query === '') {
      this.reset();
      return;
    }

    // Hide recent section during search
    if (this.hasRecentSectionTarget) {
      this.recentSectionTarget.classList.add('hidden');
    }

    let visibleCount = 0;
    this.itemTargets.forEach((item) => {
      const searchText = this.normalize(item.dataset.searchText || '');
      const matches = searchText.includes(query);
      item.classList.toggle('hidden', !matches);

      // Highlight matching text using safe DOM methods
      this.updateItemHighlight(item, matches ? query : null);

      if (matches) visibleCount++;
    });

    // Show/hide empty state
    this.emptyStateTarget.classList.toggle('hidden', visibleCount > 0);

    // Update result count
    this.resultCountTarget.textContent = `${visibleCount} résultat${visibleCount !== 1 ? 's' : ''}`;
    this.resultCountTarget.classList.remove('hidden');

    // Auto-highlight first result per spec
    this.highlightIndex = visibleCount > 0 ? 0 : -1;
    this.updateKeyboardHighlight();
  }

  reset() {
    this.itemTargets.forEach((item) => {
      item.classList.remove('hidden');
      this.updateItemHighlight(item, null);
    });

    if (this.hasRecentSectionTarget) {
      this.recentSectionTarget.classList.remove('hidden');
    }

    this.emptyStateTarget.classList.add('hidden');
    this.resultCountTarget.classList.add('hidden');
    this.highlightIndex = -1;
    this.updateKeyboardHighlight();
  }

  handleKeydown(event) {
    const visibleItems = this.visibleItems;

    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        this.highlightIndex = Math.min(this.highlightIndex + 1, visibleItems.length - 1);
        this.updateKeyboardHighlight();
        break;
      case 'ArrowUp':
        event.preventDefault();
        this.highlightIndex = Math.max(this.highlightIndex - 1, -1);
        this.updateKeyboardHighlight();
        break;
      case 'Enter':
        event.preventDefault();
        if (this.highlightIndex >= 0 && visibleItems[this.highlightIndex]) {
          visibleItems[this.highlightIndex].click();
        }
        break;
      case 'Escape':
        // Let the modal controller handle Escape
        break;
    }
  }

  // Only items from "Tous les marchés" that are not hidden
  get visibleItems() {
    return this.itemTargets.filter((item) => !item.classList.contains('hidden'));
  }

  updateKeyboardHighlight() {
    const visibleItems = this.visibleItems;
    visibleItems.forEach((item, index) => {
      if (index === this.highlightIndex) {
        item.classList.add('bg-primary-50', 'border-l-3', 'border-l-primary-500');
        item.scrollIntoView({ block: 'nearest' });
        item.setAttribute('aria-selected', 'true');
      } else {
        item.classList.remove('bg-primary-50', 'border-l-3', 'border-l-primary-500');
        item.setAttribute('aria-selected', 'false');
      }
    });
  }

  /**
   * Highlights matching text within an item using safe DOM methods (no innerHTML).
   * Pass null for query to clear highlights.
   */
  updateItemHighlight(item, query) {
    const textElements = item.querySelectorAll('[data-contract-selector-target="itemText"]');

    textElements.forEach((el) => {
      if (!el.dataset.originalText) {
        el.dataset.originalText = el.textContent;
      }

      const original = el.dataset.originalText;

      if (!query) {
        el.textContent = original;
        return;
      }

      const normalizedOriginal = this.normalize(original);
      const index = normalizedOriginal.indexOf(query);

      if (index === -1) {
        el.textContent = original;
        return;
      }

      // Build highlighted content using safe DOM methods
      const fragment = document.createDocumentFragment();

      if (index > 0) {
        fragment.appendChild(document.createTextNode(original.substring(0, index)));
      }

      const mark = document.createElement('mark');
      mark.className = 'bg-warning-100 rounded px-0.5';
      mark.textContent = original.substring(index, index + query.length);
      fragment.appendChild(mark);

      const after = original.substring(index + query.length);
      if (after) {
        fragment.appendChild(document.createTextNode(after));
      }

      el.textContent = '';
      el.appendChild(fragment);
    });
  }

  /**
   * Normalize text for accent-insensitive search (French: é→e, è→e, ç→c, etc.)
   */
  normalize(text) {
    return text
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '');
  }
}
